/**
 * ToonCrafter Test Page - 2D Cartoon Animation Interpolation
 * 
 * How it works:
 * 1. Upload START frame (your 2D character in pose A)
 * 2. Upload END frame (same character in pose B)
 * 3. ToonCrafter generates smooth animation between them
 * 4. Art style is preserved - no photorealistic drift!
 * 
 * Access via: /tooncrafter-test (secret, unlisted route)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Loader2, Image as ImageIcon, Film,
  Upload, Trash2, Play, Pause,
  Sparkles, Check, X, AlertTriangle, Settings2,
  Download, ArrowLeft, Eye, EyeOff, Info, 
  ArrowRight, Clapperboard, RefreshCw
} from "lucide-react";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

export default function ToonCrafterTest() {
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state - the two keyframes
  const [startImageUrl, setStartImageUrl] = useState("");
  const [endImageUrl, setEndImageUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [loop, setLoop] = useState(false);
  const [interpolate, setInterpolate] = useState(true);
  const [colorCorrection, setColorCorrection] = useState(true);
  
  // Resolution settings - 512x320 is optimal (native training resolution)
  const [maxWidth, setMaxWidth] = useState(512);
  const [maxHeight, setMaxHeight] = useState(320);
  
  // Upload refs
  const startImageRef = useRef(null);
  const endImageRef = useRef(null);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  
  const pollRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadJobHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get("/tooncrafter-test/config");
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to load config:", error);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await api.get("/tooncrafter-test/jobs?limit=10");
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error("Failed to load job history:", error);
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/uploads/temp", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      return URL.createObjectURL(file);
    }
  };

  const handleStartImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.loading("Uploading start frame...");
    const url = await uploadFile(file);
    setStartImageUrl(url);
    toast.dismiss();
    toast.success("Start frame uploaded");
  };

  const handleEndImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.loading("Uploading end frame...");
    const url = await uploadFile(file);
    setEndImageUrl(url);
    toast.dismiss();
    toast.success("End frame uploaded");
  };

  const handleGenerate = async () => {
    if (!startImageUrl) {
      toast.error("Please upload a start frame");
      return;
    }
    if (!endImageUrl) {
      toast.error("Please upload an end frame");
      return;
    }
    
    setGenerating(true);
    setJobStatus(null);
    
    try {
      const response = await api.post("/tooncrafter-test/generate", {
        start_image_url: startImageUrl,
        end_image_url: endImageUrl,
        prompt: prompt.trim() || "",
        negative_prompt: negativePrompt.trim() || "",
        loop,
        interpolate,
        color_correction: colorCorrection,
        max_width: maxWidth,
        max_height: maxHeight
      });
      
      if (response.data.success) {
        toast.success("Animation generation started!");
        startPolling(response.data.job_id);
      } else {
        throw new Error(response.data.message || "Failed to start generation");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start generation");
      setGenerating(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    
    const poll = async () => {
      try {
        const response = await api.get(`/tooncrafter-test/status/${jobId}`);
        setJobStatus(response.data);
        
        if (response.data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.success("Animation complete!");
          loadJobHistory();
        } else if (response.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.error(response.data.error_message || "Animation failed");
          loadJobHistory();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };
    
    poll();
    pollRef.current = setInterval(poll, 3000);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/tooncrafter-test/jobs/${jobId}`);
      setJobHistory(prev => prev.filter(j => j.job_id !== jobId));
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleDownloadVideo = async (videoUrl, jobId) => {
    try {
      const response = await fetch(getAssetUrl(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tooncrafter_${jobId.slice(0, 8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download video");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Clapperboard className="w-5 h-5 text-amber-400" />
                <h1 className="text-lg font-semibold">ToonCrafter Test Lab</h1>
                <Badge variant="outline" className="text-amber-400 border-amber-400/50 text-xs">
                  Secret
                </Badge>
              </div>
            </div>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">
              2D Animation
            </Badge>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Banner */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-zinc-300 space-y-1">
                    <p className="font-medium text-amber-300">2D Cartoon Animation - Style Preserved!</p>
                    <p className="text-zinc-400 text-xs">
                      Upload your character in two poses (start & end). ToonCrafter interpolates 
                      smooth animation between them while <strong className="text-amber-400">preserving your exact 2D art style</strong> - 
                      no morphing, no photorealistic drift!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Keyframe Input Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Film className="w-4 h-4 text-amber-400" />
                  Keyframes
                </CardTitle>
                <CardDescription className="text-xs text-zinc-500">
                  Upload the same character in two different poses
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Frame */}
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400 flex items-center gap-2">
                      <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">A</span>
                      Start Frame
                    </Label>
                    <div 
                      className="aspect-square rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-amber-500/50 transition-colors"
                      onClick={() => startImageRef.current?.click()}
                      data-testid="start-image-upload"
                    >
                      {startImageUrl ? (
                        <img src={getAssetUrl(startImageUrl)} alt="Start Frame" className="w-full h-full object-contain" />
                      ) : (
                        <div className="text-center p-4">
                          <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                          <p className="text-xs text-zinc-500">Pose A</p>
                        </div>
                      )}
                    </div>
                    <input 
                      ref={startImageRef} 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleStartImageUpload} 
                    />
                    {startImageUrl && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-[10px] h-7 text-zinc-400 hover:text-red-400" 
                        onClick={() => setStartImageUrl("")}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>

                  {/* Arrow indicator */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center justify-center pointer-events-none">
                    <ArrowRight className="w-6 h-6 text-amber-400" />
                  </div>

                  {/* End Frame */}
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400 flex items-center gap-2">
                      <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">B</span>
                      End Frame
                    </Label>
                    <div 
                      className="aspect-square rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-amber-500/50 transition-colors"
                      onClick={() => endImageRef.current?.click()}
                      data-testid="end-image-upload"
                    >
                      {endImageUrl ? (
                        <img src={getAssetUrl(endImageUrl)} alt="End Frame" className="w-full h-full object-contain" />
                      ) : (
                        <div className="text-center p-4">
                          <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                          <p className="text-xs text-zinc-500">Pose B</p>
                        </div>
                      )}
                    </div>
                    <input 
                      ref={endImageRef} 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleEndImageUpload} 
                    />
                    {endImageUrl && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-[10px] h-7 text-zinc-400 hover:text-red-400" 
                        onClick={() => setEndImageUrl("")}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>

                {/* Visual flow indicator */}
                <div className="flex items-center justify-center gap-2 mt-4 py-2 border-t border-zinc-800">
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <span className="bg-green-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px]">A</span>
                    <span>Start</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-green-600 via-amber-500 to-blue-600 mx-2" />
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <span className="bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px]">B</span>
                    <span>End</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Motion Description */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  Motion Description (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  placeholder="Describe the motion... e.g., 'character jumping energetically' or 'smooth walking motion'"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[60px] bg-zinc-800/50 border-zinc-700 text-sm"
                  disabled={generating}
                  data-testid="motion-prompt-input"
                />
                <Textarea
                  placeholder="Negative prompt (things to avoid)... e.g., 'blurry, distorted, morphing'"
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  className="min-h-[40px] bg-zinc-800/50 border-zinc-700 text-sm"
                  disabled={generating}
                  data-testid="negative-prompt-input"
                />
              </CardContent>
            </Card>

            {/* Animation Settings */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-amber-400" />
                  Animation Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Loop Toggle */}
                <div className="flex items-center justify-between py-2">
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />
                    Loop Animation
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Creates a seamless looping video</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Switch
                    checked={loop}
                    onCheckedChange={setLoop}
                    disabled={generating}
                  />
                </div>

                {/* 2x Interpolation Toggle */}
                <div className="flex items-center justify-between py-2">
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    2x Smooth Interpolation (FILM)
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Doubles frame count for smoother animation</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Switch
                    checked={interpolate}
                    onCheckedChange={setInterpolate}
                    disabled={generating}
                  />
                </div>

                {/* Color Correction Toggle */}
                <div className="flex items-center justify-between py-2">
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    Color Correction
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Fixes color inconsistencies between frames. Disable if colors look wrong.</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Switch
                    checked={colorCorrection}
                    onCheckedChange={setColorCorrection}
                    disabled={generating}
                  />
                </div>

                {/* Resolution Presets */}
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">
                    Resolution
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">512x320 is optimal (model's training resolution)</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant={maxWidth === 512 && maxHeight === 320 ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setMaxWidth(512); setMaxHeight(320); }}
                      disabled={generating}
                      className={`text-[10px] h-8 ${maxWidth === 512 && maxHeight === 320 ? "bg-amber-600" : ""}`}
                    >
                      512x320 (Best)
                    </Button>
                    <Button
                      variant={maxWidth === 512 && maxHeight === 512 ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setMaxWidth(512); setMaxHeight(512); }}
                      disabled={generating}
                      className={`text-[10px] h-8 ${maxWidth === 512 && maxHeight === 512 ? "bg-amber-600" : ""}`}
                    >
                      512x512
                    </Button>
                    <Button
                      variant={maxWidth === 768 && maxHeight === 512 ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setMaxWidth(768); setMaxHeight(512); }}
                      disabled={generating}
                      className={`text-[10px] h-8 ${maxWidth === 768 && maxHeight === 512 ? "bg-amber-600" : ""}`}
                    >
                      768x512 (HD)
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1">Current: {maxWidth}x{maxHeight}px</p>
                </div>
              </CardContent>
            </Card>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !startImageUrl || !endImageUrl}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-medium"
              data-testid="generate-tooncrafter-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Animation...
                </>
              ) : (
                <>
                  <Clapperboard className="w-5 h-5 mr-2" />
                  Generate Animation
                </>
              )}
            </Button>

            {/* Job Status */}
            {jobStatus && (
              <Card className={`border ${
                jobStatus.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                jobStatus.status === "failed" ? "border-red-500/50 bg-red-500/5" :
                "border-amber-500/50 bg-amber-500/5"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {jobStatus.status === "completed" ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : jobStatus.status === "failed" ? (
                        <X className="w-4 h-4 text-red-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                      )}
                      <span className="text-sm font-medium capitalize">{jobStatus.status}</span>
                    </div>
                    <span className="text-xs text-zinc-500">{Math.round(jobStatus.progress_percent)}%</span>
                  </div>
                  
                  <Progress value={jobStatus.progress_percent} className="h-2 mb-2" />
                  <p className="text-xs text-zinc-400">{jobStatus.progress_message}</p>
                  
                  {jobStatus.status === "completed" && jobStatus.video_url && (
                    <div className="mt-4">
                      <video 
                        src={getAssetUrl(jobStatus.video_url)} 
                        controls 
                        loop 
                        autoPlay 
                        muted
                        className="w-full rounded-lg bg-black" 
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => handleDownloadVideo(jobStatus.video_url, jobStatus.job_id)}
                        data-testid="download-result-btn"
                      >
                        <Download className="w-3 h-3 mr-1" /> Download Animation
                      </Button>
                    </div>
                  )}
                  
                  {jobStatus.status === "failed" && jobStatus.error_message && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                      {jobStatus.error_message}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - History & Tips */}
          <div className="space-y-4">
            {/* History */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Film className="w-4 h-4 text-amber-400" />
                    History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="h-6 w-6 p-0">
                    {showHistory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="p-0">
                  <ScrollArea className="h-[350px]">
                    {jobHistory.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">No animations yet</div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {jobHistory.map((job) => (
                          <div key={job.job_id} className="p-3 hover:bg-zinc-800/30 transition-colors">
                            <div className="flex items-start gap-2 mb-2">
                              <Badge variant="outline" className={`text-[10px] ${
                                job.status === "completed" ? "text-green-400 border-green-500/50" :
                                job.status === "failed" ? "text-red-400 border-red-500/50" :
                                "text-amber-400 border-amber-500/50"
                              }`}>
                                {job.status}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                {new Date(job.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="text-[10px] text-zinc-500 mb-2">
                              {job.max_width || 512}x{job.max_height || 512}px
                              {job.loop && " • Loop"}
                              {job.interpolate && " • 2x FILM"}
                            </div>
                            {job.status === "completed" && job.video_url && (
                              <video 
                                src={getAssetUrl(job.video_url)} 
                                controls 
                                loop 
                                muted
                                className="w-full rounded-lg bg-black mb-2" 
                              />
                            )}
                            <div className="flex gap-2">
                              {job.status === "completed" && job.video_url && (
                                <Button variant="ghost" size="sm" className="flex-1 text-[10px] h-7 text-green-400" onClick={() => handleDownloadVideo(job.video_url, job.job_id)}>
                                  <Download className="w-3 h-3 mr-1" /> Download
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="text-[10px] h-7 text-red-400" onClick={() => handleDeleteJob(job.job_id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* How It Works */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-amber-400" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="bg-amber-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">1</span>
                  <p>Upload <strong className="text-amber-400">start frame</strong> (character in pose A)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-amber-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">2</span>
                  <p>Upload <strong className="text-amber-400">end frame</strong> (same character in pose B)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-amber-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">3</span>
                  <p>ToonCrafter generates <strong className="text-green-400">smooth animation</strong> between them</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-amber-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">4</span>
                  <p>Your <strong className="text-amber-400">2D art style is preserved</strong> - no AI drift!</p>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Tips for Best Results
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-1">
                <p>• Use the <strong className="text-amber-400">same character</strong> in both frames</p>
                <p>• Keep <strong className="text-amber-400">consistent lighting</strong> and background</p>
                <p>• Poses should be <strong className="text-amber-400">different but related</strong></p>
                <p>• <strong className="text-amber-400">Simple, clear poses</strong> work best</p>
                <p>• More frames = <strong className="text-amber-400">smoother animation</strong></p>
                <p>• ~$0.07 per generation</p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
